import { useState, useEffect, useCallback } from 'react';
import { Users, Crown, Star, CircleAlert as AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MemberDashboard from '../../components/territory/MemberDashboard';
import LeaderDashboard from '../../components/territory/LeaderDashboard';
import ExecutiveDashboard from '../../components/territory/ExecutiveDashboard';
import type {
  TerritoryAnalyticsSnapshot,
  TerritorySegmentConfig,
  WhitespaceSuggestion,
  Account,
} from '../../types';

type DashboardView = 'member' | 'leader' | 'executive';

interface MemberSnapshot extends TerritoryAnalyticsSnapshot {
  user_name: string;
  user_email: string;
}

interface ViewingMember {
  userId: string;
  name: string;
  snapshot: TerritoryAnalyticsSnapshot | null;
  history: TerritoryAnalyticsSnapshot[];
  accounts: Account[];
}

interface TerritoryDashboardResponse {
  setup_complete: boolean;
  my_snapshot: TerritoryAnalyticsSnapshot | null;
  my_history: TerritoryAnalyticsSnapshot[];
  my_accounts: Account[];
  team_snapshots: MemberSnapshot[];
  team_accounts: Account[];
  org_avg_health: number;
}

interface MemberSnapshotsResponse {
  snapshot: TerritoryAnalyticsSnapshot | null;
  history: TerritoryAnalyticsSnapshot[];
}

export default function TerritoryAnalytics() {
  const { user, organisation, membership } = useAuth();
  const { apiPost, apiGet } = useApi();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<DashboardView>('member');
  const [mySnapshot, setMySnapshot] = useState<TerritoryAnalyticsSnapshot | null>(null);
  const [myHistory, setMyHistory] = useState<TerritoryAnalyticsSnapshot[]>([]);
  const [myAccounts, setMyAccounts] = useState<Account[]>([]);
  const [teamSnapshots, setTeamSnapshots] = useState<MemberSnapshot[]>([]);
  const [allSnapshots, setAllSnapshots] = useState<MemberSnapshot[]>([]);
  const [segmentConfig, setSegmentConfig] = useState<TerritorySegmentConfig | null>(null);
  const [whitespaceSuggestions, setWhitespaceSuggestions] = useState<WhitespaceSuggestion[]>([]);
  const [teamAccounts, setTeamAccounts] = useState<Account[]>([]);
  const [allOrgAccounts, setAllOrgAccounts] = useState<Account[]>([]);
  const [orgAvgHealth, setOrgAvgHealth] = useState(0);
  const [setupMissing, setSetupMissing] = useState(false);
  const [viewingMember, setViewingMember] = useState<ViewingMember | null>(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const role = membership?.role;
  const isLeader = role === 'leader' || role === 'admin';
  const isExecutive = role === 'executive';

  useEffect(() => {
    if (!user?.id || !organisation?.id) return;
    if (isExecutive) setView('executive');
    else if (isLeader) setView('leader');
    else setView('member');
  }, [user?.id, organisation?.id, role, isLeader, isExecutive]);

  useEffect(() => {
    if (!user?.id || !organisation?.id) return;
    loadData();
  }, [user?.id, organisation?.id, view]);

  async function loadData() {
    setLoading(true);

    const dash = await apiGet<TerritoryDashboardResponse>(
      `/v1/territory/dashboard?org_id=${organisation!.id}&view=${view}`,
    ).catch(() => null);

    if (!dash || !dash.setup_complete) {
      setSetupMissing(true);
      setLoading(false);
      return;
    }
    setSetupMissing(false);

    setMySnapshot(dash.my_snapshot);
    setMyHistory(dash.my_history);
    setMyAccounts(dash.my_accounts);

    if (view === 'leader') {
      setTeamSnapshots(dash.team_snapshots);
      setTeamAccounts(dash.team_accounts);
      setOrgAvgHealth(dash.org_avg_health);

      const { config: seg } = await apiGet<{ config: TerritorySegmentConfig | null }>(
        `/v1/territory/segment-config?org_id=${organisation!.id}`,
      ).catch(() => ({ config: null as TerritorySegmentConfig | null }));
      setSegmentConfig(seg);

      const { suggestions } = await apiGet<{ suggestions: WhitespaceSuggestion[] }>(
        `/v1/whitespace-suggestions?org_id=${organisation!.id}&status=suggested`,
      ).catch(() => ({ suggestions: [] as WhitespaceSuggestion[] }));
      setWhitespaceSuggestions(suggestions);
    }

    if (view === 'executive') {
      setAllSnapshots(dash.team_snapshots);
      setAllOrgAccounts(dash.team_accounts);
      setOrgAvgHealth(dash.org_avg_health);
    }

    setLoading(false);
  }

  const handleRefresh = useCallback(async () => {
    if (!organisation?.id || refreshing) return;
    setRefreshing(true);
    try {
      await apiPost('/v1/tier-accounts', { org_id: organisation.id, mode: 'lightweight' });
      await loadData();
    } catch { /* best effort */ }
    setRefreshing(false);
  }, [organisation?.id, refreshing, apiPost]);

  const loadWhitespaceSuggestions = useCallback(async () => {
    if (!organisation?.id) return;
    const { suggestions } = await apiGet<{ suggestions: WhitespaceSuggestion[] }>(
      `/v1/whitespace-suggestions?org_id=${organisation.id}&status=suggested`,
    ).catch(() => ({ suggestions: [] as WhitespaceSuggestion[] }));
    setWhitespaceSuggestions(suggestions);
  }, [organisation?.id, apiGet]);

  const handleViewMember = useCallback(async (memberId: string, memberName: string) => {
    if (!organisation?.id) return;
    setMemberLoading(true);

    const { snapshot, history } = await apiGet<MemberSnapshotsResponse>(
      `/v1/territory/member-snapshots?org_id=${organisation.id}&user_id=${memberId}`,
    ).catch(() => ({ snapshot: null as TerritoryAnalyticsSnapshot | null, history: [] as TerritoryAnalyticsSnapshot[] }));

    const { accounts } = await apiGet<{ accounts: Account[] }>(
      `/v1/team-member-accounts?org_id=${organisation.id}&user_id=${memberId}`,
    ).catch(() => ({ accounts: [] as Account[] }));

    setViewingMember({
      userId: memberId,
      name: memberName,
      snapshot,
      history,
      accounts,
    });
    setMemberLoading(false);
  }, [organisation?.id, apiGet]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (setupMissing) {
    return (
      <div className="app-page">
        <p className="app-kicker mb-2">Territory intelligence</p>
        <h1 className="font-display text-3xl font-semibold text-app-ink sm:text-4xl">Territory Analytics</h1>
        <p className="mt-2 text-sm text-app-muted">Evidence-led territory management.</p>
        <div className="max-w-lg mx-auto mt-16 text-center">
          <div className="w-16 h-16 bg-app-warning/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-app-warning" />
          </div>
          <h2 className="text-lg font-semibold text-app-ink mb-2">Organisation Setup Required</h2>
          <p className="text-sm text-app-muted leading-relaxed">
            Territory analytics requires your organisation intelligence to be completed first.
            Ask an admin to complete the vendor intelligence setup in Configuration before using this feature.
          </p>
        </div>
      </div>
    );
  }

  const tabs: { key: DashboardView; label: string; icon: typeof Users; show: boolean }[] = [
    { key: 'member', label: 'My Territory', icon: Users, show: true },
    { key: 'leader', label: 'Team View', icon: Crown, show: isLeader },
    { key: 'executive', label: 'Theatre View', icon: Star, show: isExecutive },
  ];

  return (
    <div className="app-page">
      <div className="mb-6 flex flex-col gap-5 border-b border-app-line pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="app-kicker mb-2">Territory intelligence</p>
          <h1 className="font-display text-3xl font-semibold text-app-ink sm:text-4xl">Territory Analytics</h1>
          <p className="mt-2 text-sm text-app-muted">Evidence-led territory management.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-app-muted bg-app-surface border border-app-line rounded-lg hover:bg-app-canvas hover:border-app-line-strong transition disabled:opacity-50"
            title="Re-score accounts and refresh data"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Updating...' : 'Update'}
          </button>
        <div className="flex items-center gap-1 border border-app-line bg-app-surface p-1" role="tablist" aria-label="Territory views">
          {tabs.filter(t => t.show).map(tab => (
            <button
              key={tab.key}
              onClick={() => { setView(tab.key); setViewingMember(null); }}
              role="tab"
              aria-selected={view === tab.key}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition ${
                view === tab.key
                  ? 'bg-app-surface text-app-ink shadow-none'
                  : 'text-app-muted hover:text-app-ink'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
        </div>
      </div>

      {memberLoading && (
        <div className="flex items-center justify-center h-40">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!memberLoading && viewingMember && (view === 'leader' || view === 'executive') && (
        <div>
          <button
            onClick={() => setViewingMember(null)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 mb-4 text-sm font-medium text-forest-700 bg-forest-50 rounded-lg hover:bg-forest-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {view === 'leader' ? 'Team View' : 'Theatre View'}
          </button>
          <p className="text-sm text-app-muted mb-4">Viewing territory for <span className="font-semibold text-app-ink">{viewingMember.name}</span></p>
          <MemberDashboard
            snapshot={viewingMember.snapshot}
            history={viewingMember.history}
            accounts={viewingMember.accounts}
          />
        </div>
      )}

      {!memberLoading && !viewingMember && view === 'member' && (
        <MemberDashboard
          snapshot={mySnapshot}
          history={myHistory}
          accounts={myAccounts}
        />
      )}

      {!memberLoading && !viewingMember && view === 'leader' && (
        <LeaderDashboard
          mySnapshot={mySnapshot}
          teamSnapshots={teamSnapshots}
          teamAccounts={teamAccounts}
          segmentConfig={segmentConfig}
          whitespaceSuggestions={whitespaceSuggestions}
          orgAvgHealth={orgAvgHealth}
          orgId={organisation!.id}
          userId={user!.id}
          userEmail={user!.email || ''}
          onSegmentSaved={() => loadData()}
          onViewMember={handleViewMember}
          onSuggestionsChanged={loadWhitespaceSuggestions}
        />
      )}

      {!memberLoading && !viewingMember && view === 'executive' && (
        <ExecutiveDashboard
          mySnapshot={mySnapshot}
          allSnapshots={allSnapshots}
          allAccounts={allOrgAccounts}
          orgAvgHealth={orgAvgHealth}
          orgId={organisation!.id}
          onViewMember={handleViewMember}
        />
      )}
    </div>
  );
}
